import { formatAnalysisMoney } from "../lib/moneyDisplay";
import { formatCompactIsoCurrency } from "@/revenue-intelligence/lib/formatCurrency";
import { getDealOwner } from "@revelate/utils";
import { getFullName } from "@revelate/utils";
import { defineRevenueIntelligenceAnalysisContract } from "../lib/revenue-intelligence-analysis-contract";
import type {
  RevenueIntelligenceMetricContract,
} from "../lib/revenue-intelligence-analysis-contract";
import {
  getPipelineRiskDriverCopy,
  getPipelineRiskExecutiveCopy,
  getPipelineRiskManagementActionCopy,
  getPipelineRiskManagementActionSupportingCopy,
  getPipelineRiskManagementCopy,
  getPipelineRiskPositionCopy,
} from "./pipelineRiskInsightCopy";
import {
  buildPipelineRiskL2Insights,
  type PipelineRiskConcentration,
} from "./pipelineRiskInsights";
import {
  buildPipelineRiskBubbleChartPoints,
  getPipelineRiskDealDrivers,
  selectPipelineRiskKeyDeals,
  selectPipelineRiskRankedDeals,
  selectPipelineRiskBubbleDeals,
  type StateOfSalesV2PipelineRiskAnalysis,
} from "./pipelineAnalysis";
import type {
  RevenueIntelligenceChatDataset,
  RevenueIntelligenceChatRecord,
  RevenueIntelligenceChatRecordField,
} from "../components/RevenueIntelligenceChatContext";
import type { PipelineRiskMovement } from "./pipelineRiskMovement";

type CompanyCurrency = Parameters<typeof formatCompactIsoCurrency>[1];

type UrgencyDeal = {
  id: number | string;
  urgency_bucket?: "high" | "medium" | "low";
  urgency_score?: number;
};

const pipelineRiskChatRecordFields: RevenueIntelligenceChatRecordField[] = [
  {
    field: "deal",
    label: "Deal",
    aliases: ["deal", "deal name", "opportunity"],
    semanticRoles: ["identifier"],
    type: "category",
    sortable: true,
    filterable: true,
  },
  {
    field: "owner",
    label: "Owner",
    aliases: ["owner", "deal owner", "sales rep", "ägare", "säljare"],
    type: "category",
    sortable: true,
    filterable: true,
  },
  {
    field: "deal_value",
    label: "Deal value",
    aliases: ["deal value", "value", "amount", "affärsvärde", "värde"],
    type: "currency",
    metricId: "state_of_sales.pipeline_risk.deal_value",
    sortable: true,
  },
  {
    field: "pipeline_share",
    label: "Share of open pipeline",
    aliases: [
      "pipeline share",
      "share of pipeline",
      "share of open pipeline",
      "andel av pipeline",
      "andel av öppen pipeline",
      "hur stor del av pipeline",
    ],
    type: "percentage",
    metricId: "state_of_sales.pipeline_risk.pipeline_share",
    sortable: true,
  },
  {
    field: "pipeline_risk_score",
    label: "Pipeline Risk score",
    aliases: ["risk", "risk score", "risk points", "riskpoäng"],
    type: "number",
    metricId: "state_of_sales.pipeline_risk.score",
    sortable: true,
  },
  {
    field: "forecast_contribution",
    label: "Forecast contribution",
    aliases: ["forecast", "forecast contribution", "prognosbidrag"],
    type: "currency",
    metricId: "state_of_sales.pipeline_risk.forecast_exposure",
    sortable: true,
  },
  {
    field: "stage",
    label: "Pipeline stage",
    aliases: ["stage", "pipeline stage", "fas", "steg"],
    type: "category",
    sortable: true,
    filterable: true,
  },
  {
    field: "expected_close",
    label: "Revelate Expected Close",
    aliases: ["expected close", "close date", "förväntat avslut"],
    type: "date",
    sortable: true,
  },
  {
    field: "primary_risk",
    label: "Primary risk factor",
    aliases: ["primary risk", "main risk", "largest risk", "största risken", "riskfaktor"],
    type: "category",
    sortable: false,
  },
  {
    field: "risk_evidence",
    label: "Why the deal is at risk",
    aliases: ["why", "risk reason", "risk evidence", "varför", "riskorsak"],
    semanticRoles: ["evidence", "explanation"],
    type: "category",
    sortable: false,
  },
  {
    field: "recommended_action",
    label: "Recommended action",
    aliases: ["action", "next move", "what to do", "åtgärd", "nästa steg"],
    semanticRoles: ["recommendation"],
    type: "category",
    sortable: false,
  },
];

function formatExpectedCloseDate(value: unknown) {
  if (!value) return null;
  const date = new Date(value as string | number | Date);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

export function getCustomerFacingPipelineRiskEvidence(explanation: string) {
  const emailRatio = explanation.match(/^Email ratio of ([\d.]+)$/i);
  if (emailRatio?.[1]) {
    return `The seller sent ${emailRatio[1]} emails for every buyer email`;
  }
  const lastModified = explanation.match(/^Last modified (\d+) days ago$/i);
  if (lastModified?.[1]) return `No CRM update for ${lastModified[1]} days`;
  const lastInteraction = explanation.match(/^Last interaction (\d+) days ago$/i);
  if (lastInteraction?.[1]) return `No buyer interaction for ${lastInteraction[1]} days`;
  if (/^Part of the top 20% of deals with >50% of pipeline value\.?$/i.test(explanation)) {
    return "This deal is among the 20% of deals that together represent more than half of pipeline value";
  }
  return explanation;
}

function addressRecommendedAction(action: string, ownerName: string) {
  if (!action || action === "Not specified" || ownerName === "Not specified") return action;
  return action.replace(/\bthe sales rep\b/gi, ownerName);
}

function buildPipelineRiskChatRecord(
  deal: StateOfSalesV2PipelineRiskAnalysis["deals"][number],
  companyCurrency: CompanyCurrency,
): RevenueIntelligenceChatRecord {
  const expectedCloseDate = formatExpectedCloseDate(deal.adjustedCloseDate);
  const expectedCloseInstant = deal.adjustedCloseDate
    ? new Date(deal.adjustedCloseDate)
    : null;
  const expectedCloseIso = expectedCloseInstant
    && !Number.isNaN(expectedCloseInstant.getTime())
    ? expectedCloseInstant.toISOString()
    : null;
  const owner = getDealOwner(deal);
  const ownerName = owner ? getFullName(owner) : "Not specified";
  const dealValue = Number(deal.value ?? 0);
  const pipelineShare = Number(deal.concentrationPct ?? 0);
  const riskScore = Math.round(Number(deal.weighted_risk_score ?? 0));
  const forecastContribution = Number(deal.forecast_contribution ?? 0);
  const riskDrivers = getPipelineRiskDealDrivers(deal);
  const primaryDriver = riskDrivers[0] ?? null;
  const riskEvidence = riskDrivers.slice(0, 3).map(driver => (
    driver.explanation
      ? `${driver.label}: ${getCustomerFacingPipelineRiskEvidence(driver.explanation)}`
      : driver.label
  )).join("; ");
  const recommendedAction = addressRecommendedAction(
    primaryDriver?.recommendedAction ?? "Not specified",
    ownerName,
  );

  return {
    id: String(deal.id),
    label: String(deal.name ?? "Unnamed deal"),
    details: [
      formatCompactIsoCurrency(dealValue, companyCurrency),
      `${riskScore} pts`,
      deal.stage_label ? String(deal.stage_label) : null,
      expectedCloseDate ? `expected ${expectedCloseDate}` : null,
    ].filter((value): value is string => Boolean(value)),
    values: {
      deal: String(deal.name ?? "Unnamed deal"),
      owner: ownerName,
      deal_value: dealValue,
      pipeline_share: pipelineShare,
      pipeline_risk_score: riskScore,
      forecast_contribution: forecastContribution,
      stage: deal.stage_label ? String(deal.stage_label) : "Not specified",
      expected_close: expectedCloseIso,
      primary_risk: primaryDriver?.label ?? "Not specified",
      risk_evidence: riskEvidence || "Not specified",
      recommended_action: recommendedAction,
    },
    displayValues: {
      deal: String(deal.name ?? "Unnamed deal"),
      owner: ownerName,
      deal_value: formatCompactIsoCurrency(dealValue, companyCurrency),
      pipeline_share: `${pipelineShare.toFixed(1)}%`,
      pipeline_risk_score: `${riskScore} pts`,
      forecast_contribution: formatCompactIsoCurrency(
        forecastContribution,
        companyCurrency,
      ),
      stage: deal.stage_label ? String(deal.stage_label) : "Not specified",
      expected_close: expectedCloseDate ?? "Not specified",
      primary_risk: primaryDriver?.label ?? "Not specified",
      risk_evidence: riskEvidence || "Not specified",
      recommended_action: recommendedAction,
    },
  };
}

export function buildStateOfSalesForecastTimelineChatDataset(
  chartData: Array<{
    actual_last_year: number | null;
    is_context: boolean;
    month: string;
    target: number;
    total_minus_risk: number;
    expected_close_v2?: number | null;
    crm_forecast?: number;
    won: number;
  }>,
  metricIdPrefix: string,
): RevenueIntelligenceChatDataset {
  return {
    datasetId: `${metricIdPrefix}.timeline`,
    label: "Forecast and Target over time",
    description: "The exact monthly series used by the visible State of Sales Forecast chart.",
    dimensions: [
      { field: "month", label: "Month", type: "date" },
      { field: "period_scope", label: "Period scope", type: "category" },
    ],
    measures: [
      {
        field: "forecast",
        label: "Revelate forecast",
        metricId: `${metricIdPrefix}.total`,
        type: "currency",
      },
      {
        field: "crm_forecast",
        label: "CRM forecast",
        metricId: `${metricIdPrefix}.crm_forecast`,
        type: "currency",
      },
      {
        field: "target",
        label: "Target",
        metricId: `${metricIdPrefix}.target`,
        type: "currency",
      },
      {
        field: "won",
        label: "Won",
        metricId: `${metricIdPrefix}.won`,
        type: "currency",
      },
      {
        field: "actual_last_year",
        label: "Actual last year",
        metricId: `${metricIdPrefix}.actual_last_year`,
        type: "currency",
      },
    ],
    rows: chartData.map(row => ({
      month: row.month,
      period_scope: row.is_context ? "Context" : "Selected period",
      forecast: row.expected_close_v2 ?? row.total_minus_risk,
      crm_forecast: row.crm_forecast ?? 0,
      target: row.target,
      won: row.won,
      actual_last_year: row.actual_last_year,
    })),
    allowedChartTypes: ["line"],
    defaultChart: {
      type: "line",
      title: "Forecast and Target over time",
      xField: "month",
      yFields: ["forecast", "target"],
      groupBy: null,
      showConclusion: true,
    },
    sourceHelper: "useStateOfSalesV2Forecast",
    sourceFields: [
      "forecastChartDataWithContext.month",
      "forecastChartDataWithContext.total_minus_risk",
      "forecastChartDataWithContext.expected_close_v2",
      "forecastChartDataWithContext.crm_forecast",
      "forecastChartDataWithContext.target",
      "forecastChartDataWithContext.won",
      "priorYearActualByCurrentMonth",
    ],
    caveats: [
      "Context-period rows support orientation and do not enter selected-period headline metrics.",
    ],
  };
}

export function buildStateOfSalesForecastChatMetrics({
  companyCurrency,
  historical,
  metricIdPrefix,
  position,
}: {
  companyCurrency: CompanyCurrency;
  historical: boolean;
  metricIdPrefix: string;
  position: {
    forecast: number;
    target: number;
    won: number;
  };
}): RevenueIntelligenceMetricContract[] {
  return [
    {
      metricId: `${metricIdPrefix}.total`,
      label: "Forecast",
      aliases: ["forecast total", "expected to close", "forecast position", "prognos"],
      valueLabel: formatAnalysisMoney(position.forecast, companyCurrency),
      definition: "Won revenue plus the approved probability- and risk-adjusted contribution from open deals in the selected period.",
      includedRecords: "Won deals and selected-period open deals included by the reviewed Forecast curve.",
      dateField: "Actual closed date for won deals; Revelate expected close date for open deals.",
      valueBasis: "Entered won Deal value plus approved Forecast contribution for open deals.",
      currencyHandling: "Normalized to the company default currency before aggregation.",
      probabilityOrRiskBasis: "Uses the same reviewed probability and Forecast risk basis as the visible Forecast curve.",
      sourceFields: ["forecastPosition.forecast", "forecastPosition.won", "forecastPosition.expectedFromOpenDeals"],
      caveats: historical
        ? ["Historical periods use Won versus Target and do not present a forward Forecast."]
        : undefined,
    },
    {
      metricId: `${metricIdPrefix}.target`,
      label: "Target",
      aliases: ["forecast target", "revenue target", "target"],
      valueLabel: formatAnalysisMoney(position.target, companyCurrency),
      definition: "The selected-period Target emitted by the same reviewed Forecast helper as the visible curve.",
      includedRecords: "The configured company Target for the selected Forecast period.",
      dateField: "Selected Forecast period.",
      valueBasis: "Company Target value.",
      currencyHandling: "Company default currency.",
      probabilityOrRiskBasis: "No probability or risk weighting is applied.",
      sourceFields: ["forecastPosition.target", "forecastChartData.target"],
    },
    {
      metricId: `${metricIdPrefix}.won`,
      label: "Won revenue",
      aliases: ["won", "won revenue", "closed won"],
      valueLabel: formatAnalysisMoney(position.won, companyCurrency),
      definition: "Entered Deal value from actual closed-won deals included by the reviewed Forecast helper.",
      includedRecords: "Closed-won deals in the selected period.",
      dateField: "Actual closed date.",
      valueBasis: "Normalized entered Deal value.",
      currencyHandling: "Normalized to the company default currency before aggregation.",
      probabilityOrRiskBasis: "No probability or risk weighting is applied.",
      sourceFields: ["forecastPosition.won", "forecastChartData.won"],
    },
    {
      metricId: `${metricIdPrefix}.actual_last_year`,
      label: "Actual last year",
      aliases: ["last year actual", "prior year won", "actual last year"],
      valueLabel: "Monthly closed-won reference",
      definition: "Entered Deal value from actual closed-won deals in the equivalent month one year earlier.",
      includedRecords: "Closed-won deals returned by the reviewed prior-year actuals query.",
      dateField: "Actual closed date shifted twelve months for chart alignment.",
      valueBasis: "Normalized entered Deal value.",
      currencyHandling: "Normalized through the existing Revenue Intelligence deal-preparation helper.",
      probabilityOrRiskBasis: "No probability or risk weighting is applied.",
      sourceFields: ["priorYearActualByCurrentMonth", "deal.closed_at", "deal.value"],
      caveats: ["This is a historical reference series, not a Forecast snapshot."],
    },
  ];
}

export function buildStateOfSalesPipelineRiskChatContract({
  analysis,
  companyCurrency,
  companyId,
  concentrations = [],
  movement,
  pageLabel,
  periodLabel,
  filtersLabel,
  selectedPeriodRange,
  totalForecast,
  urgencyDeals,
}: {
  analysis: StateOfSalesV2PipelineRiskAnalysis;
  companyCurrency: CompanyCurrency;
  companyId: string;
  concentrations?: PipelineRiskConcentration[];
  movement?: PipelineRiskMovement | null;
  pageLabel: string;
  periodLabel?: string;
  filtersLabel?: string;
  selectedPeriodRange?: { from: Date | string; to: Date | string } | null;
  totalForecast: number;
  urgencyDeals: UrgencyDeal[];
}) {
  const urgencyById = new Map(
    urgencyDeals.map(deal => [String(deal.id), deal]),
  );
  const urgentRiskDeals = analysis.deals.flatMap((deal) => {
    const urgency = urgencyById.get(String(deal.id));
    if (!urgency) return [];
    return [{
      ...deal,
      urgency_bucket: urgency.urgency_bucket,
      urgency_score: urgency.urgency_score,
    }];
  });
  const insights = buildPipelineRiskL2Insights({
    analysis,
    concentrations,
    movement,
    selectedPeriodRange,
    totalForecast,
    urgentDeals: urgentRiskDeals,
  });
  const executiveCopy = getPipelineRiskExecutiveCopy(insights, companyCurrency);
  const positionCopy = getPipelineRiskPositionCopy(insights, companyCurrency);
  const driverCopy = getPipelineRiskDriverCopy(insights, companyCurrency);
  const managementCopy = getPipelineRiskManagementCopy(insights);
  const leadingAction = insights.managementAttention.actions[0];
  const keyDeals = selectPipelineRiskKeyDeals(
    analysis.deals,
    urgencyDeals,
    totalForecast,
  );
  const rankedDeals = selectPipelineRiskRankedDeals(analysis.deals);
  const bubbleDeals = selectPipelineRiskBubbleDeals(
    analysis.deals,
    urgencyDeals,
  );
  const bubblePoints = buildPipelineRiskBubbleChartPoints(bubbleDeals);

  return defineRevenueIntelligenceAnalysisContract({
    contractId: "state_of_sales.pipeline_risk",
    analysisLabel: "State of Sales Pipeline Risk",
    accessPolicy: {
      allowedRoles: ["super_admin", "admin"],
      companyId,
      dataScope: "company",
      enforcement: "company_role_gate",
    },
    productLens: "Where open pipeline and Forecast are exposed to approved Pipeline Risk signals.",
    requiredAccess: "revenue_intelligence.pipeline_risk",
    sourceHelper: "buildPipelineRiskL2Insights",
    scope: {
      pageLabel,
      periodLabel,
      filtersLabel,
    },
    metrics: [
      {
        metricId: "state_of_sales.pipeline_risk.forecast_exposure",
        label: "Forecast exposure by Pipeline Risk",
        aliases: ["forecast exposure", "risk exposure", "pipeline risk exposure"],
        valueLabel: formatCompactIsoCurrency(analysis.exposedForecastValue, companyCurrency),
        definition: "Forecast contribution from selected-period open deals carrying approved Pipeline Risk signals.",
        includedRecords: "Selected-period open deals included by the reviewed Pipeline Risk analysis.",
        dateField: "Revelate expected close date.",
        valueBasis: "Approved Forecast contribution.",
        currencyHandling: "Normalized to the company default currency before aggregation.",
        probabilityOrRiskBasis: "Uses existing Pipeline Risk severity and approved Forecast contribution helpers.",
        sourceFields: ["severity.forecastValue", "exposedForecastValue"],
      },
      {
        metricId: "state_of_sales.pipeline_risk.deal_value",
        label: "Deal value by Pipeline Risk",
        aliases: ["deal value risk exposure", "pipeline value by risk"],
        valueLabel: formatCompactIsoCurrency(analysis.exposedOpenDealValue, companyCurrency),
        definition: "Entered Deal value across selected-period open deals carrying approved Pipeline Risk signals.",
        includedRecords: "Selected-period open deals included by the reviewed Pipeline Risk analysis.",
        dateField: "Revelate expected close date.",
        valueBasis: "Normalized entered Deal value.",
        currencyHandling: "Normalized to the company default currency before aggregation.",
        probabilityOrRiskBasis: "Risk severity uses the existing weighted Pipeline Risk score; Deal value is not probability weighted.",
        sourceFields: ["severity.rawValue", "exposedOpenDealValue"],
      },
      {
        metricId: "state_of_sales.pipeline_risk.pipeline_share",
        label: "Share of open pipeline",
        aliases: [
          "pipeline share",
          "share of pipeline",
          "share of open pipeline",
          "andel av pipeline",
          "andel av öppen pipeline",
          "hur stor del av pipeline",
        ],
        valueLabel: "Per open deal",
        definition: "The open Deal's normalized entered value divided by normalized entered Deal value across the selected-period open pipeline.",
        includedRecords: "Selected-period open deals returned by the existing Pipeline Risk evaluation.",
        dateField: "Revelate expected close date.",
        valueBasis: "Normalized entered Deal value.",
        currencyHandling: "Both the Deal value and denominator use the company default currency.",
        probabilityOrRiskBasis: "No probability or risk weighting is applied.",
        sourceFields: ["concentrationPct", "value"],
      },
      {
        metricId: "state_of_sales.pipeline_risk.score",
        label: "Pipeline Risk score",
        aliases: ["risk score", "pipeline risk points"],
        valueLabel: "0–100 points per deal",
        definition: "The existing weighted Pipeline Risk score displayed by State of Sales.",
        includedRecords: "Deals selected by the existing Pipeline Risk bubble-chart selector.",
        dateField: "Current approved risk assessment for each selected-period open deal.",
        valueBasis: "Points, not monetary value.",
        currencyHandling: "Not applicable.",
        probabilityOrRiskBasis: "Uses the existing weighted Pipeline Risk score without rebucketing.",
        sourceFields: ["weighted_risk_score"],
      },
      {
        metricId: "state_of_sales.pipeline_risk.stage_probability",
        label: "Stage probability",
        aliases: ["stage probability", "deal probability"],
        valueLabel: "0–100% per deal",
        definition: "The approved adjusted stage probability used by the existing Pipeline Risk bubble chart.",
        includedRecords: "Deals selected by the existing Pipeline Risk bubble-chart selector.",
        dateField: "Current deal stage.",
        valueBasis: "Percentage.",
        currencyHandling: "Not applicable.",
        probabilityOrRiskBasis: "Uses adjusted stage probability, with the existing stage probability as fallback.",
        sourceFields: ["stage_probability_adjusted", "stage_probability"],
      },
    ],
    chat: {
      availability: "available",
      datasets: [
        {
          datasetId: "state_of_sales.pipeline_risk.severity",
          label: "Pipeline Risk by severity",
          description: "The exact severity rows used by the visible Pipeline Risk analysis.",
          dimensions: [
            { field: "severity", label: "Risk severity", type: "category" },
          ],
          measures: [
            {
              field: "forecast_exposure",
              label: "Forecast exposure",
              metricId: "state_of_sales.pipeline_risk.forecast_exposure",
              type: "currency",
            },
            {
              field: "deal_value",
              label: "Deal value",
              metricId: "state_of_sales.pipeline_risk.deal_value",
              type: "currency",
            },
            {
              field: "deal_count",
              label: "Deals",
              type: "number",
            },
          ],
          rows: analysis.severity.map(row => ({
            severity: row.label,
            forecast_exposure: row.forecastValue,
            deal_value: row.rawValue,
            deal_count: row.count,
          })),
          allowedChartTypes: ["bar"],
          defaultChart: {
            type: "bar",
            title: "Pipeline Risk by severity",
            xField: "severity",
            yFields: ["forecast_exposure"],
            groupBy: null,
            showConclusion: true,
          },
          sourceHelper: "buildStateOfSalesV2PipelineRiskAnalysis",
          sourceFields: ["severity.key", "severity.forecastValue", "severity.rawValue", "severity.count"],
        },
        {
          datasetId: "state_of_sales.pipeline_risk.deal_map",
          label: "Deal Risk and Stage probability",
          description: "The same approved deal population and values as the visible Pipeline Risk bubble chart.",
          dimensions: [
            { field: "deal", label: "Deal", type: "category" },
          ],
          measures: [
            {
              field: "stage_probability",
              label: "Stage probability",
              metricId: "state_of_sales.pipeline_risk.stage_probability",
              type: "percentage",
            },
            {
              field: "pipeline_risk",
              label: "Pipeline Risk",
              metricId: "state_of_sales.pipeline_risk.score",
              type: "number",
            },
            {
              field: "deal_value",
              label: "Deal value",
              metricId: "state_of_sales.pipeline_risk.deal_value",
              type: "currency",
            },
          ],
          rows: bubblePoints.map(deal => ({
            deal: String(deal.name ?? "Unnamed deal"),
            stage_probability: deal.stageProbability,
            pipeline_risk: deal.pipelineRisk,
            deal_value: Number(deal.value ?? 0),
          })),
          allowedChartTypes: ["scatter"],
          defaultChart: {
            type: "scatter",
            title: "Deal Risk and Stage probability",
            xField: "stage_probability",
            yFields: ["pipeline_risk", "deal_value"],
            groupBy: "deal",
            showConclusion: true,
          },
          sourceHelper: "buildPipelineRiskBubbleChartPoints",
          sourceFields: [
            "name",
            "weighted_risk_score",
            "stage_probability_adjusted",
            "stage_probability",
            "value",
            "concentrationPct",
          ],
          caveats: [
            "Deal value controls bubble size and does not change Pipeline Risk or probability.",
          ],
        },
      ],
      topics: [
        "pipeline risk",
        "high risk",
        "risk exposure",
        "risk",
        "högrisk",
        "riskexponering",
      ],
      suggestedQuestions: [
        "Which deals have the greatest risk?",
        "What needs management attention?",
        "Why is Pipeline Risk exposed?",
        "What should we do?",
      ],
      insights: [
        {
          insightId: "state_of_sales.pipeline_risk.executive_summary",
          label: "Pipeline Risk position",
          title: executiveCopy.conclusion,
          body: executiveCopy.explanation
            ?? positionCopy.supporting
            ?? positionCopy.heading,
          evidence: executiveCopy.evidence
            ?? positionCopy.supporting
            ?? positionCopy.heading,
          sourceFields: ["currentPosition", "severity"],
        },
        {
          insightId: "state_of_sales.pipeline_risk.driver",
          label: driverCopy.label,
          title: driverCopy.heading,
          body: driverCopy.supporting ?? driverCopy.heading,
          evidence: driverCopy.supporting ?? driverCopy.heading,
          sourceFields: ["drivers", "themes"],
        },
        {
          insightId: "state_of_sales.pipeline_risk.management_attention",
          label: managementCopy.label,
          title: managementCopy.heading,
          body: managementCopy.supporting
            ?? (leadingAction
              ? getPipelineRiskManagementActionSupportingCopy(
                leadingAction,
                companyCurrency,
              )
              : managementCopy.heading),
          evidence: leadingAction
            ? getPipelineRiskManagementActionSupportingCopy(
              leadingAction,
              companyCurrency,
            ) || managementCopy.heading
            : managementCopy.supporting ?? managementCopy.heading,
          recommendedAction: leadingAction
            ? getPipelineRiskManagementActionCopy(leadingAction)
            : undefined,
          sourceFields: ["managementAttention"],
        },
      ],
      recordSets: [
        {
          recordSetId: "state_of_sales.pipeline_risk.ranked_deals",
          label: "Deals ranked by Pipeline Risk score",
          aliases: [
            "deals with the greatest risk",
            "deals with highest risk",
            "highest risk deals",
            "highest risk score",
            "riskiest deals",
            "deals med störst risk",
            "deals med högst risk",
            "mest riskfyllda deals",
            "störst risk",
            "högst risk",
            "högst riskpoäng",
            "oavsett risknivå",
            "oavsett om de är high medium low",
          ],
          responseTitle: "Deals — highest Pipeline Risk score first",
          fields: pipelineRiskChatRecordFields,
          defaultSort: {
            field: "pipeline_risk_score",
            direction: "desc",
          },
          records: rankedDeals.map(deal => (
            buildPipelineRiskChatRecord(deal, companyCurrency)
          )),
          rankingDescription: "Includes every selected-period open deal carrying an approved Pipeline Risk signal. Ranked by Pipeline Risk score, then normalized entered Deal value and Forecast contribution.",
          sourceHelper: "selectPipelineRiskRankedDeals",
          sourceLabel: "State of Sales · Pipeline Risk",
          sourceFields: [
            "value",
            "weighted_risk_score",
            "forecast_contribution",
            "stage_label",
            "adjustedCloseDate",
            "users.owner",
            "risks.*.risk",
            "risks.*.risk_explanation",
            "risks.*.suggested_next_move",
          ],
          emptyMessage: "No deals with Pipeline Risk signals are available in the selected period.",
          caveats: [
            "Deal value is shown as entered value in the company default currency; it is not Forecast contribution.",
            "Share of open pipeline uses selected-period open pipeline as its denominator.",
          ],
        },
        {
          recordSetId: "state_of_sales.pipeline_risk.high_risk_deals",
          label: "High-risk deals",
          aliases: [
            "high-risk deals",
            "high risk deals",
            "högriskdeals",
            "högriskaffärer",
          ],
          responseTitle: "High-risk deals — ranked by Deal value",
          fields: pipelineRiskChatRecordFields,
          defaultSort: {
            field: "deal_value",
            direction: "desc",
          },
          records: keyDeals.map(deal => (
            buildPipelineRiskChatRecord(deal, companyCurrency)
          )),
          rankingDescription: "Includes open deals with a weighted Pipeline Risk score of 50 points or more. Ranked by normalized entered Deal value, then risk score and Forecast contribution.",
          sourceHelper: "selectPipelineRiskKeyDeals",
          sourceLabel: "State of Sales · Pipeline Risk",
          sourceFields: [
            "value",
            "concentrationPct",
            "weighted_risk_score",
            "forecast_contribution",
            "stage_label",
            "adjustedCloseDate",
            "users.owner",
            "risks.*.risk",
            "risks.*.risk_explanation",
            "risks.*.suggested_next_move",
          ],
          emptyMessage: "No High-risk deals are available in the selected period.",
          caveats: [
            "Deal value is shown as entered value in the company default currency; it is not Forecast contribution.",
            "Share of open pipeline uses selected-period open pipeline as its denominator.",
          ],
        },
      ],
    },
  });
}
